import { useHomeContextUser } from "../../homeContext.ts";
import { useFetchers } from "react-router-dom";
import {
  UserPreferenceKey,
  ActualUserPreference,
} from "../../../../common/api/decimal/methods/user.ts";
import { getBasePathWithTrailingSlash } from "../../../../common/env.ts";
import { useUserPreferenceFetcher } from "./user.ts";
import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Warning: The backend can encounter race conditions if you do parallel updates
 */
export default function useUserPreference<T = unknown>(
  preferenceId?: UserPreferenceKey
): [
  T | undefined,
  (preferenceValue: NonNullable<T>) => void,
  () => void,
  Date | undefined,
] {
  const user = useHomeContextUser();
  const currentPreferencesObject = preferenceId
    ? (user.preferences[preferenceId] as ActualUserPreference<T>)
    : undefined;

  const myFetcher = useUserPreferenceFetcher();
  // activeFetcher may equal myFetcher, but it might not if you use
  // this hook with the same key in two different places
  const activeFetcher = useFetchers().find(
    (f) =>
      f.formAction ===
      `${getBasePathWithTrailingSlash()}actions/user/preferences/${preferenceId}`
  );

  // useFetcher is very high churn and noisy in hooks
  // store it in a ref so the update/delete functions are stable and can be
  // hook dependencies in consumers
  const myFetcherRef = useRef(myFetcher);
  useEffect(() => {
    myFetcherRef.current = myFetcher;
  }, [myFetcher]);

  const updateValue = useCallback(
    (value: NonNullable<T>) => {
      if (preferenceId) {
        // note simple string causes encoding error think it needs wrapping in quotes
        myFetcherRef.current.submit(
          typeof value === "string" ? JSON.stringify(value) : value,
          {
            action: `/actions/user/preferences/${preferenceId}`,
            method: "PUT",
            encType: "application/json",
          }
        );
      }
    },
    [preferenceId]
  );

  const deleteValue = useCallback(() => {
    if (preferenceId) {
      myFetcherRef.current.submit(
        {},
        {
          action: `/actions/user/preferences/${preferenceId}`,
          method: "DELETE",
        }
      );
    }
  }, [preferenceId]);

  let currentValue = currentPreferencesObject?.value;
  if (activeFetcher && activeFetcher.state !== "idle") {
    if (activeFetcher.formMethod === "PUT" && activeFetcher.json) {
      currentValue = activeFetcher.json as NonNullable<T>;
    } else if (activeFetcher.formMethod === "DELETE") {
      currentValue = undefined;
    }
  }

  const updatedDate = useMemo(() => {
    return currentPreferencesObject?.updatedAt
      ? new Date(currentPreferencesObject?.updatedAt)
      : undefined;
  }, [currentPreferencesObject]);

  return [currentValue, updateValue, deleteValue, updatedDate];
}
